'use client';

import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  Link,
  SimpleGrid,
  Tag,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import slugify from 'react-slugify';

import { ReactComponent as PromotionTag } from '@/app/_icons/promotion-tag.svg';
import { useOpenFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import { ImageTransformationKeys } from '@/types/files/files.types';
import { NewCarData, NewCarListing } from '@/types/new-cars/new-cars.types';

import ButtonCustom from '../button/ButtonCustom';
import CustomImage from '../custom-image/CustomImage';
import LinkCustom from '../link/LinkCustom';
import { PriceFormat } from '../price-format/price-format';

import Styles from './NewVehiclesCard.module.scss';

type Props = {
  vehicle?: NewCarListing;
  priority?: boolean;
  newCars?: NewCarData[];
  pageType?: VehiclePageType;
};
export type VehiclePageType = 'newCars' | 'buyOnline';
const NewVehiclesCard = ({
  vehicle,
  priority = false,
  newCars,
  pageType = 'newCars',
}: Props) => {
  const [isHover, setHover] = useState(false);
  const openLeadForm = useOpenFloatingCTA();
  const router = useRouter();

  const handleCardClick = () => {
    if (vehicle?.main_title && pageType === 'newCars') {
      router.push(`/new-cars/${vehicle.slug}`);
    } else if (vehicle?.main_title && pageType === 'buyOnline') {
      router.push(`/buy-online/${vehicle?.slug}`);
    }
  };

  const handlePromotionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!vehicle) return;
    const promotionSlug = vehicle.range?.name && slugify(vehicle.range.name);

    if (vehicle.promotionalSpecials?.length === 1) {
      router.push(
        `/promotions/${promotionSlug}/${vehicle.promotionalSpecials[0]?.id}`,
      );
    } else if (promotionSlug) {
      router.push(`/promotions/${promotionSlug}`);
    }
  };

  const showPrimaryCTA =
    pageType === 'newCars' ? !vehicle?.hide_in_build_and_price : true;

  const imageKeys: ImageTransformationKeys = {
    desktopKey: 'new-car-landingpage-desktop',
    mobileKey: 'new-car-landingpage-mobile',
  };

  const primaryCTAText =
    pageType === 'buyOnline' ? 'Buy Online' : 'Build yours';
  const primaryCTALink =
    pageType === 'buyOnline'
      ? `/buy-online/${vehicle?.slug}?mmcode=${vehicle?.lowestSpecMmCode || ''}`
      : `/build-price/${vehicle?.slug}`;

  return (
    <Card
      role="link"
      className={Styles.vehicleCard}
      maxW={{ base: '100%', sm: '352px' }}
      width={'100%'}
      shadow={0}
      borderRadius={0}
      padding={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      cursor="pointer"
      onClick={handleCardClick}
    >
      <CardHeader
        padding={0}
        className={Styles.cardHeader}
        display={'flex'}
        flexDirection={'row'}
      >
        <Box className={Styles.carInfo} maxW={'210'} width={'100%'}>
          <Box className={Styles.modelName}>{vehicle?.main_title}</Box>
          <Text className={Styles.description}>
            {vehicle?.intro_subheading}
          </Text>
        </Box>
        <Box className={Styles.carPriceInfo} marginLeft={'auto'}>
          <Flex direction={'column'} mb={'10px'}>
            {vehicle?.price_from ? <span className={Styles.priceFrom}>From</span> : null }
            {vehicle ? (
              <>
                {vehicle?.price_from  ? <Box className={Styles.carPrice}>
                  <PriceFormat
                    prefix="R"
                    value={vehicle?.price_from || 'N/A'}
                    suffix={
                      vehicle?.price_disclaimer &&
                      vehicle?.price_disclaimer?.length > 0
                        ? '*'
                        : undefined
                    }
                  />
                </Box> : null }
                {vehicle?.price_disclaimer ? (
                  <Text color={'#697279'} fontSize={12}>
                    {vehicle?.price_disclaimer}
                  </Text>
                ) : null}
              </>
            ) : null}
          </Flex>
          <Flex direction={'column'} gap={1}>
            {vehicle?.isPromotional ? (
              <Tag
                borderRadius={0}
                className={Styles.promotionTag}
                onClick={handlePromotionClick}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
              >
                <PromotionTag />
                <span>Promotion</span>
              </Tag>
            ) : null}
          </Flex>
        </Box>
      </CardHeader>
      <CardBody padding={0} my={4}>
        <Link className={Styles.carLink}>
          {isHover ? (
            <CustomImage
              imageSrc={
                getImageLink(vehicle?.image_no_background_side ?? null) || ''
              }
              className={Styles.carImage}
              imageKeys={imageKeys}
              width={322}
              height={193}
              alt={vehicle?.image_alt_tag || vehicle?.main_title || 'car image'}
              additionalParams={{
                fit: 'inside',
              }}
            />
          ) : (
            <CustomImage
              imageSrc={
                getImageLink(vehicle?.image_no_background_left_angle ?? null) ||
                ''
              }
              imageKeys={imageKeys}
              className={Styles.carImage}
              width={322}
              height={193}
              alt={vehicle?.image_alt_tag || vehicle?.main_title || 'car image'}
              loading={priority ? 'eager' : 'lazy'}
              additionalParams={{
                fit: 'inside',
              }}
            />
          )}
        </Link>
      </CardBody>
      <CardFooter
        padding={0}
        flexDir={'column'}
        className={Styles.cardFooter}
        overflow={'hidden'}
      >
        <Flex
          className={Styles.carDetailsWrapper}
          direction={'row'}
          justifyContent={'center'}
          width={'100%'}
        >
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>Fuel Type</Text>
            <Text className={Styles.carUnit}>
              <strong>{vehicle?.lowestSpecfuelType}</strong>
            </Text>
          </Box>
          <Divider
            orientation="vertical"
            borderLeftWidth={'1px'}
            marginX={{ base: 2, sm: 3 }}
            opacity={1}
            className="borderLeft"
          />
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>Fuel Consumption</Text>
            <Text className={Styles.carUnit}>
              <strong>
                {vehicle?.images_fuel_consumption}
                <span>L/100km</span>
              </strong>
            </Text>
          </Box>
          <Divider
            orientation="vertical"
            borderLeftWidth={'1px'}
            marginX={{ base: 2, sm: 3 }}
            opacity={1}
            className="borderLeft"
          />
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>
              {vehicle?.co2_emissions_heading}
            </Text>
            <Text className={Styles.carUnit}>
              <strong>{vehicle?.co2_emissions}</strong>
            </Text>
          </Box>
        </Flex>
        <SimpleGrid
          columns={[1, 1, 2, 2]}
          spacing={[2]}
          mt={{ base: 6, md: 0 }}
          mb={{ base: 4, md: 0 }}
          className={Styles.onHoverCTA}
        >
          {showPrimaryCTA ? (
            <LinkCustom
              href={primaryCTALink}
              className={'btnOutline'}
              moduleClass={Styles.ctaBtn}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {primaryCTAText}
            </LinkCustom>
          ) : (
            <ButtonCustom
              btnText="Get in Touch"
              onClick={(e) => {
                e.stopPropagation();
                if (!vehicle?.main_title) return;
                const newCar = newCars?.find(
                  (c) => slugify(c.main_title) === slugify(vehicle!.main_title),
                );

                if (!newCar) {
                  return;
                }
                openLeadForm('test-drive', 'TestDrive', {
                  type: 'new-car',
                  newCar: newCar,
                });
              }}
              className={'btnOutline'}
              moduleClass={Styles.ctaBtn}
            />
          )}
          <LinkCustom
            className={''}
            href={`/new-cars/${vehicle?.slug}`}
            moduleClass={Styles.ctaBtn}
            onClick={(e) => e.stopPropagation()}
          >
            Explore
          </LinkCustom>
        </SimpleGrid>
      </CardFooter>
    </Card>
  );
};

export default NewVehiclesCard;
